import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Tab, Tabs } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import AddMeasure from './AddMeasure';
import EditMeasure from './EditMeasure';
import {
  isEnableLoadingSelector,
  isInitSelector,
  measureListSelector,
} from './selector';
import { deleteMeasure, getMeasureList, refreshMeasureList } from './thunk';

import type { TableBaseColumnType, TableBaseRowType } from '@/components/TableBase/Body';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { MeasureJson } from '@/types/domain/measure/entities/measureEntity';
import type { UserType } from '@/types/domain/user/redux';

import AdditionalLoading from '@/components/AdditionalLoading';
import Delete from '@/components/Delete';
import TableBase from '@/components/TableBase';
import Title from '@/components/Title';
import MeasureInfo from '@/domain/measure/component/MeasureInfo';
import { ChestCircumference } from '@/domain/measure/service/entity/valueObjects/chestCircumference';
import { HeadCircumference } from '@/domain/measure/service/entity/valueObjects/headCircumference';
import { Height } from '@/domain/measure/service/entity/valueObjects/height';
import { MeasureDate } from '@/domain/measure/service/entity/valueObjects/measureDate';
import { Weight } from '@/domain/measure/service/entity/valueObjects/weight';
import { toStringDateDisplay } from '@/utils/date';

/**
 * プロパティ
 */
export type Props = {
  /** ユーザー */
  user: UserType,
  /** 子ども */
  child: ChildJson,
};

const Measure: React.FC<Props> = ({ user, child }) => {
  const isEnableCreate = false;
  const dispatch = useDispatch();
  const isEnableLoading = useSelector(isEnableLoadingSelector);
  const isInit = useSelector(isInitSelector);
  const [
    isOpen,
    setIsOpen,
  ] = React.useState<boolean>(false);
  const [index, setIndex] = React.useState(0);
  const measureList = useSelector(measureListSelector);
  const [
    deleteMeasureJson,
    setDeleteMeasureJson,
  ] = React.useState<MeasureJson | undefined>(undefined);
  const [
    editMeasureJson,
    setEditMeasureJson,
  ] = React.useState<MeasureJson | undefined>(undefined);
  React.useEffect(() => {
    if (!isInit) {
      dispatch(refreshMeasureList());
    }
  }, [dispatch, isInit]);
  const rows: TableBaseRowType[] = [];
  for (let i = 0; i < measureList.length; i += 1) {
    const measure = measureList[i];
    if (measure.data.parentId === child.parentId && measure.data.childId === child.childId) {
      const measurer = measure.data.measurerType === 'app' ? '自分' : '施設';
      rows.push({
        id: i.toString(),
        measureDate: toStringDateDisplay(new Date(measure.data.measureDate)),
        measurer,
        height: measure.data.height ? measure.data.height.toString() : '-',
        weight: measure.data.weight ? measure.data.weight.toString() : '-',
        headCircumference:
          measure.data.headCircumference
            ? measure.data.headCircumference.toString()
            : '-',
        chestCircumference:
          measure.data.chestCircumference
            ? measure.data.chestCircumference.toString()
            : '-',
        type: measure.data.measurerType,
      });
    }
  }
  if (rows.length === 0) {
    rows.push({
      id: '-',
      measureDate: '登録されていません',
      measurer: '-',
      height: '-',
      weight: '-',
      headCircumference: '-',
      chestCircumference: '-',
      type: '-',
    });
  }
  const columns: TableBaseColumnType[] = [
    { id: 'measureDate', label: MeasureDate.label, minWidth: 60 },
  ];
  if (index === 0) {
    columns.push({ id: 'height', label: Height.label, minWidth: 10 });
    columns.push({ id: 'weight', label: Weight.label, minWidth: 10 });
  } else {
    columns.push({ id: 'headCircumference', label: HeadCircumference.label, minWidth: 10 });
    columns.push({ id: 'chestCircumference', label: ChestCircumference.label, minWidth: 10 });
  }
  const actions = [
    {
      icon: <EditIcon />,
      title: '編集',
      action: (r: TableBaseRowType) => {
        const item = measureList[parseInt(r.id, 10)];
        setEditMeasureJson(item);
      },
      disabled: (r: TableBaseRowType) => r.type !== 'app',
    },
    {
      icon: <DeleteIcon />,
      title: '削除',
      action: (r: TableBaseRowType) => {
        const item = measureList[parseInt(r.id, 10)];
        setDeleteMeasureJson(item);
      },
      disabled: (r: TableBaseRowType) => r.type !== 'app',
    },
  ];
  return (
    <div>
      <Title title="成長記録" />
      <Tabs
        value={index}
        onChange={(_, newIndex) => setIndex(newIndex as number)}
        indicatorColor="secondary"
        textColor="inherit"
        variant="fullWidth"
      >
        <Tab
          label="身長・体重"
          id="weight-height"
        />
        <Tab
          label="頭囲・胸囲"
          id="circumference"
        />
      </Tabs>
      <div style={{ marginTop: '10px' }}>
        <TableBase
          rows={rows}
          columns={columns}
          actions={isEnableCreate ? actions : []}
          addAction={isEnableCreate ? () => setIsOpen(true) : undefined}
          refreshAction={() => dispatch(refreshMeasureList())}
          isPagination={false}
          isShowFilter={false}
          isShowFooter={false}
        />
        <AdditionalLoading
          isEnableLoading={isEnableLoading}
          onClick={() => dispatch(getMeasureList())}
        />
        <AddMeasure
          isOpen={isOpen}
          close={() => setIsOpen(false)}
          child={child}
          user={user}
        />
        <Delete<MeasureJson>
          comp={MeasureInfo}
          data={deleteMeasureJson}
          onDelete={(json) => {
            if (json) {
              dispatch(deleteMeasure(json));
            }
            setDeleteMeasureJson(undefined);
          }}
          title="成長記録"
          content="以下の記録を削除しますか？"
        />
        {editMeasureJson && (
          <EditMeasure
            data={editMeasureJson}
            close={() => setEditMeasureJson(undefined)}
            child={child}
            user={user}
          />
        )}
      </div>
    </div>
  );
};

export default Measure;
